import { fetchAuthSession } from "aws-amplify/auth";
import { useRef } from "react";
import { toast } from "sonner";
import { DEFAULT_API_BASE } from "../config/api";

export function useWebSocket() {
  const wsRef = useRef(null);
  const keepAliveRef = useRef(null);

  function getWebSocketBaseUrl() {
    // CRA env var convention: only REACT_APP_* is exposed to the browser.
    // Example: REACT_APP_API_BASE_URL="http://localhost:8077"
    const apiBase =
      process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE;

    if (typeof apiBase === "string" && apiBase.trim()) {
      const trimmed = apiBase.trim().replace(/\/+$/, "");
      // Convert http(s) -> ws(s)
      if (trimmed.startsWith("https://")) return `wss://${trimmed.slice("https://".length)}`;
      if (trimmed.startsWith("http://")) return `ws://${trimmed.slice("http://".length)}`;
      // If someone already provided ws(s) here, use as-is.
      if (trimmed.startsWith("wss://") || trimmed.startsWith("ws://")) return trimmed;
    }

    // Default (production) — derived from the shared constant, not a
    // separately duplicated literal (DEFAULT_API_BASE is "https://apiv1.regulify.ai").
    return DEFAULT_API_BASE.replace(/^https:\/\//, "wss://");
  }

  function buildWsUrl(url, token) {
    // Allow passing a full ws/wss URL for local dev or custom endpoints.
    if (
      typeof url === "string" &&
      (url.startsWith("ws://") || url.startsWith("wss://"))
    ) {
      return url;
    }

    const wsBase = getWebSocketBaseUrl();
    return `${wsBase}/api/v1/ws/${url}/?token=${token}`;
  }

  const connectWebSocket = async (
    url,
    payload,
    onData,
    onClose = () => { },
    onError = () => { },
  ) => {
    let ws;
    let keepAliveInterval;
    let closed = false;
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      ws = new WebSocket(buildWsUrl(url, token));
      wsRef.current = ws;
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        // Keep-alive
        keepAliveInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 20000);
        keepAliveRef.current = keepAliveInterval;
        ws.send(JSON.stringify(payload));
        toast.info("Processing started...");
      };

      ws.onmessage = (event) => {
        const msg = event.data;
        try {
          const parsed = JSON.parse(msg);
          if (parsed.type === "error") {
            toast.error(`Error: ${parsed.message}`);
            onError(); // <---- Add this line
            ws.close(4001, parsed.message); // optional but clean
            return;
          }
          if (parsed.type === "info") {
            toast.info(`Update: ${parsed.message}`);
            return;
          }
          if (onData) onData(parsed);
        } catch (error) {
          console.log(error);
        }
      };

      ws.onerror = (e) => {
        toast.error("There was a problem. Please try again later.");
        onError();
      };

      ws.onclose = (e) => {
        onClose();
        if (e && e.code) {
          console.warn(`WebSocket closed: code=${e.code}, reason=${e.reason}`);
        } else {
          console.warn("WebSocket closed");
        }
      };
    } catch (error) {
      toast.error("There was a problem. Please try again later.");
      onError();
    }
  };

  return { connectWebSocket };
}
